/**
 * Process Templates API services
 */

import apiClient from './client';
import type { ProcessTemplate, Category, Tag, User, RecurrenceRule } from '@/types';

export interface TemplateCreate {
  title: string;
  description?: string;
  recurrenceRule: RecurrenceRule;
  categoryId?: string;
  domainId?: string;
  domain_ids?: string[];
  defaultAssigneeId?: string;
  default_assignee_ids?: string[];
  outlineDocId?: string;
  outlineDocUrl?: string;
  notificationDaysBefore?: number[];
  isActive?: boolean;
  tagIds?: string[];
}

export interface TemplateUpdate {
  title?: string;
  description?: string;
  recurrenceRule?: RecurrenceRule;
  categoryId?: string | null;
  domainId?: string | null;
  domain_ids?: string[];
  defaultAssigneeId?: string | null;
  default_assignee_ids?: string[];
  outlineDocId?: string;
  outlineDocUrl?: string;
  notificationDaysBefore?: number[];
  isActive?: boolean;
  tagIds?: string[];
}

export interface TemplateWithStats extends ProcessTemplate {
  pendingCount: number;
  inProgressCount: number;
  completedCount: number;
  overdueCount: number;
  totalInstances: number;
  nextDueDate: string | null;
}

const transformCategory = (data: any): Category | undefined => {
  if (!data) return undefined;
  return {
    id: data.id,
    tenantId: data.tenant_id,
    name: data.name,
    description: data.description,
    color: data.color,
    icon: data.icon,
    sortOrder: data.sort_order,
    isActive: data.is_active,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

const transformTag = (data: any): Tag => ({
  id: data.id,
  tenantId: data.tenant_id,
  name: data.name,
  color: data.color,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
});

const transformUser = (data: any): User | undefined => {
  if (!data) return undefined;
  return {
    id: data.id,
    email: data.email || '',
    // Worker returns 'name', full API returns 'full_name'
    fullName: data.full_name || data.name || '',
    isSuperAdmin: data.is_super_admin ?? false,
    isActive: data.is_active ?? true,
    emailVerifiedAt: data.email_verified_at || null,
    lastLoginAt: data.last_login_at || null,
  };
};

// Convert snake_case recurrence_rule to camelCase
const transformRecurrenceRule = (rule: any): RecurrenceRule | null => {
  if (!rule) return null;
  if (rule.type === 'interval') {
    return {
      type: 'interval',
      every: rule.every,
      unit: rule.unit,
      startDate: rule.start_date,
      endDate: rule.end_date,
    } as RecurrenceRule;
  } else if (rule.type === 'calendar') {
    return {
      type: 'calendar',
      pattern: rule.pattern,
      startDate: rule.start_date,
      dayOfWeek: rule.day_of_week,
      dayOfMonth: rule.day_of_month,
      month: rule.month,
      endDate: rule.end_date,
    } as RecurrenceRule;
  } else if (rule.type === 'once') {
    return {
      type: 'once',
      date: rule.date,
    } as RecurrenceRule;
  }
  return rule;
};

const transformDomain = (data: any) => {
  if (!data) return undefined;
  return {
    id: data.id,
    tenantId: data.tenant_id,
    name: data.name,
    slug: data.slug,
    description: data.description,
    color: data.color || '#6366f1',
    icon: data.icon,
    isVisible: Boolean(data.is_visible),
    sortOrder: data.sort_order || 0,
    isActive: Boolean(data.is_active),
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
};

const transformTemplate = (data: any): ProcessTemplate => ({
  id: data.id,
  tenantId: data.tenant_id,
  categoryId: data.category_id,
  domainId: data.domain_id,
  title: data.title || data.name, // Backend uses 'name', map to 'title'
  description: data.description,
  recurrenceRule: transformRecurrenceRule(data.recurrence_rule) || data.recurrence_rule,
  defaultAssigneeId: data.default_assignee_id,
  outlineDocId: data.outline_doc_id,
  outlineDocUrl: data.outline_doc_url,
  notificationDaysBefore: data.notification_days_before,
  isActive: data.is_active,
  lastInstanceDate: data.last_instance_date,
  createdById: data.created_by_id,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
  category: transformCategory(data.category),
  domain: transformDomain(data.domain),
  tags: data.tags?.map(transformTag) || [],
  defaultAssignee: transformUser(data.default_assignee),
});

export const templatesApi = {
  list: async (params?: {
    isActive?: boolean;
    categoryId?: string;
    tagId?: string;
    search?: string;
  }): Promise<ProcessTemplate[]> => {
    const queryParams: any = {};
    if (params?.isActive !== undefined) queryParams.is_active = params.isActive;
    if (params?.categoryId) queryParams.category_id = params.categoryId;
    if (params?.tagId) queryParams.tag_id = params.tagId;
    if (params?.search) queryParams.search = params.search;

    const response = await apiClient.get('/v1/templates', { params: queryParams });
    // Worker returns paginated result { items, total, limit, offset }
    const items = response.data.items || response.data || [];
    return items.map(transformTemplate);
  },

  listWithStats: async (params?: { categoryId?: string }): Promise<TemplateWithStats[]> => {
    const queryParams: any = {};
    if (params?.categoryId) queryParams.category_id = params.categoryId;

    const response = await apiClient.get('/v1/templates/with-stats', { params: queryParams });
    const items = response.data.items || response.data || [];
    return items.map((data: any) => {
      // Handle both worker format (stats object) and original format
      const stats = data.stats || {};
      return {
        ...transformTemplate(data),
        pendingCount: stats.pending_count ?? data.pending_count ?? 0,
        inProgressCount: stats.in_progress_count ?? data.in_progress_count ?? 0,
        completedCount: stats.completed_count ?? data.completed_count ?? 0,
        overdueCount: stats.overdue_count ?? data.overdue_count ?? 0,
        totalInstances: stats.total_instances ?? data.total_instances ?? 0,
        nextDueDate: stats.next_due_date ?? data.next_due_date ?? null,
      };
    });
  },

  get: async (id: string): Promise<ProcessTemplate> => {
    const response = await apiClient.get(`/v1/templates/${id}`);
    return transformTemplate(response.data);
  },

  create: async (data: TemplateCreate): Promise<ProcessTemplate> => {
    // Convert recurrence rule to snake_case
    const recurrenceRule: Record<string, unknown> = {
      type: data.recurrenceRule.type,
    };
    if (data.recurrenceRule.type === 'interval') {
      recurrenceRule.every = data.recurrenceRule.every;
      recurrenceRule.unit = data.recurrenceRule.unit;
      recurrenceRule.start_date = data.recurrenceRule.startDate;
      if (data.recurrenceRule.endDate) recurrenceRule.end_date = data.recurrenceRule.endDate;
    } else if (data.recurrenceRule.type === 'calendar') {
      recurrenceRule.pattern = data.recurrenceRule.pattern;
      recurrenceRule.start_date = data.recurrenceRule.startDate;
      if (data.recurrenceRule.dayOfWeek !== undefined) recurrenceRule.day_of_week = data.recurrenceRule.dayOfWeek;
      if (data.recurrenceRule.dayOfMonth !== undefined) recurrenceRule.day_of_month = data.recurrenceRule.dayOfMonth;
      if (data.recurrenceRule.month !== undefined) recurrenceRule.month = data.recurrenceRule.month;
      if (data.recurrenceRule.endDate) recurrenceRule.end_date = data.recurrenceRule.endDate;
    } else if (data.recurrenceRule.type === 'once') {
      recurrenceRule.date = data.recurrenceRule.date;
    }

    const payload: Record<string, unknown> = {
      name: data.title, // Backend uses 'name', frontend uses 'title'
      recurrence_rule: recurrenceRule,
    };
    if (data.description !== undefined) payload.description = data.description;
    if (data.categoryId !== undefined) payload.category_id = data.categoryId;
    if (data.domainId !== undefined) payload.domain_id = data.domainId;
    if (data.domain_ids !== undefined) payload.domain_ids = data.domain_ids;
    if (data.defaultAssigneeId !== undefined) payload.default_assignee_id = data.defaultAssigneeId;
    if (data.default_assignee_ids !== undefined) payload.default_assignee_ids = data.default_assignee_ids;
    if (data.outlineDocId !== undefined) payload.outline_doc_id = data.outlineDocId;
    if (data.outlineDocUrl !== undefined) payload.outline_doc_url = data.outlineDocUrl;
    if (data.notificationDaysBefore !== undefined) payload.notification_days_before = data.notificationDaysBefore;
    if (data.isActive !== undefined) payload.is_active = data.isActive;
    if (data.tagIds !== undefined) payload.tag_ids = data.tagIds;

    const response = await apiClient.post('/v1/templates', payload);
    return transformTemplate(response.data);
  },

  update: async (id: string, data: TemplateUpdate): Promise<ProcessTemplate> => {
    const payload: Record<string, unknown> = {};
    if (data.title !== undefined) payload.name = data.title; // Backend uses 'name'
    if (data.description !== undefined) payload.description = data.description;
    if (data.recurrenceRule !== undefined) {
      // Convert recurrence rule to snake_case
      const recurrenceRule: Record<string, unknown> = {
        type: data.recurrenceRule.type,
      };
      if (data.recurrenceRule.type === 'interval') {
        recurrenceRule.every = data.recurrenceRule.every;
        recurrenceRule.unit = data.recurrenceRule.unit;
        recurrenceRule.start_date = data.recurrenceRule.startDate;
        if (data.recurrenceRule.endDate) recurrenceRule.end_date = data.recurrenceRule.endDate;
      } else if (data.recurrenceRule.type === 'calendar') {
        recurrenceRule.pattern = data.recurrenceRule.pattern;
        recurrenceRule.start_date = data.recurrenceRule.startDate;
        if (data.recurrenceRule.dayOfWeek !== undefined) recurrenceRule.day_of_week = data.recurrenceRule.dayOfWeek;
        if (data.recurrenceRule.dayOfMonth !== undefined) recurrenceRule.day_of_month = data.recurrenceRule.dayOfMonth;
        if (data.recurrenceRule.month !== undefined) recurrenceRule.month = data.recurrenceRule.month;
        if (data.recurrenceRule.endDate) recurrenceRule.end_date = data.recurrenceRule.endDate;
      } else if (data.recurrenceRule.type === 'once') {
        recurrenceRule.date = data.recurrenceRule.date;
      }
      payload.recurrence_rule = recurrenceRule;
    }
    if (data.categoryId !== undefined) payload.category_id = data.categoryId;
    if (data.domainId !== undefined) payload.domain_id = data.domainId;
    if (data.domain_ids !== undefined) payload.domain_ids = data.domain_ids;
    if (data.defaultAssigneeId !== undefined) payload.default_assignee_id = data.defaultAssigneeId;
    if (data.default_assignee_ids !== undefined) payload.default_assignee_ids = data.default_assignee_ids;
    if (data.outlineDocId !== undefined) payload.outline_doc_id = data.outlineDocId;
    if (data.outlineDocUrl !== undefined) payload.outline_doc_url = data.outlineDocUrl;
    if (data.notificationDaysBefore !== undefined) payload.notification_days_before = data.notificationDaysBefore;
    if (data.isActive !== undefined) payload.is_active = data.isActive;
    if (data.tagIds !== undefined) payload.tag_ids = data.tagIds;

    const response = await apiClient.patch(`/v1/templates/${id}`, payload);
    return transformTemplate(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/templates/${id}`);
  },

  duplicate: async (id: string, newTitle?: string): Promise<ProcessTemplate> => {
    const params = newTitle ? { new_title: newTitle } : {};
    const response = await apiClient.post(`/v1/templates/${id}/duplicate`, null, { params });
    return transformTemplate(response.data);
  },
};
